/**
 * Everything this app does differently when it is the desktop shell's renderer
 * rather than a page in a browser.
 *
 * It renders nothing. Mounted inside the router and the Apollo provider, it
 * subscribes to the two channels the shell pushes on, starts the
 * notification poller, and answers the Pomodoro timer's request for a break
 * session. With no bridge present every branch is skipped, so a
 * browser build behaves exactly as it does today.
 *
 * SSO is the one sign-in path with a desktop branch, and it lives in `sso.ts`.
 * It runs in the system browser and returns as a custom-scheme link, which
 * arrives here like any other deep link and is routed to `/auth/callback`.
 * Email OTP needs no branch: the shell serves this bundle at the environment's
 * real web-app origin, so the `__Secure-wone_refresh` cookie and the CORS
 * allowlists work untouched.
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "providers/auth";
import { PKCE_SESSION_KEY } from "providers/auth-tokens";

import { getDesktopBridge } from "./bridge";
import { routeForDeepLink } from "./deep-link";
import { checkDesktopCallback, DESKTOP_CALLBACK_MESSAGES } from "./sso";
import { usePomodoroBreakSession } from "./pomodoro/usePomodoroBreakSession";
import { useNotificationPoller } from "./use-notification-poller";

const DesktopIntegration: React.FC = () => {
  const bridge = getDesktopBridge();
  const navigate = useNavigate();
  const { user } = useAuth();

  useNotificationPoller(bridge, Boolean(user));
  usePomodoroBreakSession(bridge, Boolean(user));

  useEffect(() => {
    if (bridge === null) return undefined;

    const unsubscribeDeepLink = bridge.onDeepLink((url) => {
      const route = routeForDeepLink(url);
      if (route === null) return;
      if (route.startsWith("/auth/callback")) {
        const rejection = checkDesktopCallback(
          route,
          sessionStorage.getItem(PKCE_SESSION_KEY)
        );
        if (rejection !== null) {
          // The reason only: the link's query carries the authorization code.
          // eslint-disable-next-line no-console
          console.warn(`[desktop-sso] ignored a sign-in link: ${rejection}`);
          // With no sign-in in progress there is nothing to tell the user
          // about: the link did not come from anything they started, and any
          // web page can fire one, so a toast would only alarm a signed-in
          // learner.
          if (rejection !== "no_sign_in_in_progress") {
            toast.error(DESKTOP_CALLBACK_MESSAGES[rejection]);
          }
          return;
        }
      }
      navigate(route);
    });

    const unsubscribeClick = bridge.onNotificationClick((_id, deepLink) => {
      const route = deepLink === undefined ? null : routeForDeepLink(deepLink);
      if (route === null) return;
      navigate(route);
    });

    // Releases any link the OS delivered before this bundle finished parsing,
    // which a cold launch from a deep link always does. Called after
    // subscribing, never before.
    bridge.signalReady?.();

    return () => {
      unsubscribeDeepLink();
      unsubscribeClick();
    };
  }, [bridge, navigate]);

  return null;
};

export default DesktopIntegration;
